import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Settings from "./Settings";
import FinancialsLock from "@/components/FinancialsLock";

/**
 * Payments page — dedicated route for the payment-providers + Zelle
 * configuration cards. We render <Settings mode="payments" /> so we
 * reuse the existing card JSX and state without duplicating ~250 lines.
 *
 * Also handles the post-OAuth redirect: when Stripe/Square/PayPal sends
 * the merchant back here with `?connected=stripe` or `?error=...`, we
 * pop a toast so they know what happened. We strip the query right
 * after so a refresh doesn't fire the toast again.
 *
 * Gated behind the Financials PIN (same lock as /financials and
 * /reports/tax) because these cards hold the live processing API keys —
 * Stripe secret, Square access token, PayPal client secret. Owner-only.
 */
export default function Payments() {
  const { businessSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const connected = searchParams.get("connected");
    const err = searchParams.get("error");
    if (!connected && !err) return;

    if (connected) {
      toast.success(`${connected[0].toUpperCase() + connected.slice(1)} connected successfully — payments are live.`);
    } else if (err) {
      // Decode underscore-joined provider+message from the callback.
      const friendly = err.replace(/_/g, " ");
      toast.error(`Could not connect: ${friendly}`);
    }

    // Strip the query so a reload doesn't re-fire the toast.
    const next = new URLSearchParams(searchParams);
    next.delete("connected");
    next.delete("error");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <FinancialsLock businessSlug={businessSlug}>
      <Settings mode="payments" />
    </FinancialsLock>
  );
}
